import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, IconButton, LinearProgress, Paper, Typography } from "@mui/material";
import { useState } from "react";
import { SystemFlatSwitch } from "../../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { CollapsibleSection } from "../../../../components/UIToolkit/Panels/CollapsibleSection";
import {
	type DataLoadFile,
	type DataLoadFileKind,
	type DataLoadMode,
	formatFileSize,
	KIND_LABEL,
	KIND_ORDER,
	MODE_LABEL,
	ROSTER_ACTION_LABEL,
	type RosterAction,
} from "./consts";

const MODE_OPTIONS: DataLoadMode[] = ["transform", "direct"];
const ROSTER_ACTIONS: RosterAction[] = ["create", "update"];

type Props = {
	file: DataLoadFile;
	index: number;
	/** Every destructive or shape-changing control is frozen while the load chain holds its snapshot. */
	locked: boolean;
	onKindChange: (kind: DataLoadFileKind) => void;
	onModeChange: (mode: DataLoadMode) => void;
	onRosterActionChange: (action: RosterAction) => void;
	onPromptChange: (prompt: string) => void;
	onTabInstructionChange: (sheet: string, value: string) => void;
	onRemove: () => void;
};

export function DataLoadFileRow({
	file,
	index,
	locked,
	onKindChange,
	onModeChange,
	onRosterActionChange,
	onPromptChange,
	onTabInstructionChange,
	onRemove,
}: Props) {
	const [sheetsOpen, setSheetsOpen] = useState(false);
	const testId = `data-load-file-${file.id}`;
	const percent = file.progress ? ((file.progress.tabIndex + 1) / file.progress.totalTabs) * 100 : 0;

	return (
		<Paper variant="outlined" sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }} data-testid={testId}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
				<Box sx={{ flex: 1, minWidth: 180 }}>
					<Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{`${index + 1}. ${file.file.name}`}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{formatFileSize(file.file.size)}
					</Typography>
				</Box>

				<SystemDropdown<DataLoadFileKind>
					label="Type"
					placeholder="Pick a type"
					items={KIND_ORDER.map((kind) => ({ value: kind, label: KIND_LABEL[kind] }))}
					selectedItem={file.kind ?? undefined}
					onChange={(value) => value && onKindChange(value)}
					formControlSx={{ minWidth: 150 }}
					disabled={locked}
					data-testid={`${testId}-kind`}
				/>

				<SystemFlatSwitch<DataLoadMode>
					options={MODE_OPTIONS.map((mode) => ({ label: MODE_LABEL[mode], value: mode, disabled: locked, "data-testid": `${testId}-mode-${mode}` }))}
					activeIndexOrValue={file.mode}
					onSwitch={(option) => onModeChange(option.value)}
					delayCallback={0}
					textWrap="nowrap"
					data-testid={`${testId}-mode`}
				/>

				{file.kind === "roster" && (
					<SystemFlatSwitch<RosterAction>
						options={ROSTER_ACTIONS.map((action) => ({
							label: ROSTER_ACTION_LABEL[action],
							value: action,
							disabled: locked,
							"data-testid": `${testId}-action-${action}`,
						}))}
						activeIndexOrValue={file.rosterAction}
						onSwitch={(option) => onRosterActionChange(option.value)}
						delayCallback={0}
						textWrap="nowrap"
						data-testid={`${testId}-action`}
					/>
				)}

				<IconButton aria-label={`Remove ${file.file.name}`} onClick={onRemove} disabled={locked} data-testid={`${testId}-remove`}>
					<DeleteOutlineOutlinedIcon />
				</IconButton>
			</Box>

			{file.mode === "transform" && (
				<SystemValidationTextField
					label="Transform prompt (optional)"
					placeholder="e.g. 'The role column is called Position', 'Ignore rows where Status is Inactive'"
					value={file.prompt}
					onChange={(e) => onPromptChange(e.target.value)}
					injectedProps={{ multiline: true, minRows: 2, maxRows: 10 }}
					fullWidth
					disabled={locked}
					reserveHeightForHelperText={false}
					dataTestId={`${testId}-prompt`}
				/>
			)}

			{file.mode === "transform" && file.sheetNames.length > 1 && (
				<CollapsibleSection
					title={`Sheet-specific instructions (${file.sheetNames.length} sheets)`}
					open={sheetsOpen}
					onToggle={() => setSheetsOpen((prev) => !prev)}
					dataTestId={`${testId}-sheets`}
				>
					{file.sheetNames.map((sheet) => (
						<SystemValidationTextField
							key={sheet}
							label={`Instructions for "${sheet}" (optional)`}
							value={file.tabInstructions[sheet] ?? ""}
							onChange={(e) => onTabInstructionChange(sheet, e.target.value)}
							fullWidth
							disabled={locked}
							formControlSx={{ mb: 1 }}
							dataTestId={`${testId}-sheet-${sheet}`}
						/>
					))}
				</CollapsibleSection>
			)}

			{/* Multi-sheet Excel transforms run for minutes; this bar is the only feedback there is. */}
			{file.progress && (
				<Box data-testid={`${testId}-progress`}>
					<Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
						<Typography variant="caption" sx={{ fontWeight: 600 }}>
							{`Transforming sheets — ${file.progress.currentTab}`}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{`${file.progress.tabIndex + 1} of ${file.progress.totalTabs}`}
						</Typography>
					</Box>
					<LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 1 }} />
				</Box>
			)}
		</Paper>
	);
}
